import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import {
  Add as AddIcon, AutoAwesome as SparkleIcon, Refresh as RefreshIcon, Lock as LockIcon,
  PhotoCamera as PhotoIcon, EditOutlined as EditIcon,
  CompareArrows as CompareIcon, IosShare as ShareIcon
} from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import AvatarCropModal from 'components/shared/modals/AvatarCropModal';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import ProgressCharts from 'components/progress/physical/ProgressCharts';
import ProgressCompareModal from 'components/progress/physical/ProgressCompareModal';
import ProgressShareModal from 'components/progress/physical/ProgressShareModal';
import ProgressEntryCard from 'components/progress/physical/ProgressEntryCard';
import ProgressObservations from 'components/progress/physical/ProgressObservations';
import { createSnack } from 'common/utils/general';
import { PARAM_DEFS } from 'common/constants/physicalParams';
import uiSlice from 'store/modules/ui';
import CURATED_PHRASES from 'common/constants/motivationalPhrases';
import { MOTIVATIONAL_PHRASES } from 'api/graphql/motivationalPhrases';
import {
  MY_PHYSICAL_PROGRESS, SUBMIT_PHYSICAL_PROGRESS, UPDATE_PHYSICAL_PROGRESS, DELETE_PHYSICAL_PROGRESS
} from 'api/graphql/physicalProgress';

// Las 3 vistas que se suben por entrada.
const PHOTO_SLOTS = [
  { key: 'front', label: 'FRENTE' },
  { key: 'lateral', label: 'LATERAL' },
  { key: 'back', label: 'ESPALDA' }
];

const PHRASE_KEY = 'otx_phys_phrase';

/* ----------------------------- Banner motivacional ----------------------------- */
function PhraseBanner() {
  const { data } = useQuery(MOTIVATIONAL_PHRASES, { fetchPolicy: 'cache-and-network' });
  const phrases = useMemo(() => {
    const backend = (data?.motivationalPhrases ?? []).map((p) => ({ text: p.text, author: p.author || 'OTRIX' }));
    return [...CURATED_PHRASES, ...backend];
  }, [data]);

  const [idx, setIdx] = useState(() => Math.floor(Math.random() * CURATED_PHRASES.length));
  const [own, setOwn] = useState(() => window.localStorage.getItem(PHRASE_KEY) || '');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(own);

  const saveOwn = () => {
    const v = draft.trim();
    setOwn(v);
    if (v) window.localStorage.setItem(PHRASE_KEY, v);
    else window.localStorage.removeItem(PHRASE_KEY);
    setEditing(false);
  };

  const current = own ? { text: own, author: 'Tú' } : (phrases[idx % phrases.length] || phrases[0]);

  return (
    <PremiumCard innerSx={{ p: { xs: 2.5, md: 3 } }} sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <SparkleIcon sx={{ color: 'gold.main', mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, md: 22 }, lineHeight: 1.3, fontStyle: 'italic' }}>
            “{current.text}”
          </Typography>
          <Typography sx={{ mt: 0.75, color: 'gold.main', fontWeight: 700, letterSpacing: '0.02em' }}>
            — {current.author}
          </Typography>
          {!editing ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {!own && (
                <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={() => setIdx((i) => (i + 1) % phrases.length)} sx={{ color: 'text.secondary', textTransform: 'none' }}>
                  Otra frase
                </Button>
              )}
              <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => { setDraft(own); setEditing(true); }} sx={{ color: 'secondary.main', textTransform: 'none' }}>
                {own ? 'Editar mi frase' : 'Escribir la mía'}
              </Button>
            </Stack>
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
              <TextField size="small" fullWidth value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Tu frase para ti" inputProps={{ maxLength: 120 }} />
              <Button variant="contained" color="primary" onClick={saveOwn} sx={{ textTransform: 'none', fontWeight: 700 }}>Guardar</Button>
            </Stack>
          )}
        </Box>
      </Stack>
    </PremiumCard>
  );
}

/* --------------------------------- Formulario --------------------------------- */
function EntryForm({ open, onClose, onSaved, entry = null }) {
  const dispatch = useDispatch();
  const isEdit = Boolean(entry);
  const [files, setFiles] = useState({}); // { front, lateral, back } — solo fotos NUEVAS
  const [crop, setCrop] = useState({ open: false, src: null, target: null });
  const [values, setValues] = useState(() => {
    if (!entry) return {};
    const init = {};
    PARAM_DEFS.forEach((p) => { if (entry[p.key] != null) init[p.key] = String(entry[p.key]); });
    return init;
  });
  const [note, setNote] = useState(entry?.note || '');
  const [date, setDate] = useState(entry?.taken_at || '');
  const [submit, { loading: creating }] = useMutation(SUBMIT_PHYSICAL_PROGRESS);
  const [update, { loading: updating }] = useMutation(UPDATE_PHYSICAL_PROGRESS);
  const loading = creating || updating;

  // En edición, cada hueco muestra la foto existente hasta que se suba una nueva.
  const previews = useMemo(() => {
    const out = {};
    PHOTO_SLOTS.forEach((s) => {
      out[s.key] = files[s.key] ? URL.createObjectURL(files[s.key]) : (entry?.[`${s.key}_image_url`] || null);
    });
    return out;
  }, [files, entry]);

  const pickFile = (slot) => (e) => {
    const f = e.target.files?.[0];
    if (f) setCrop({ open: true, src: URL.createObjectURL(f), target: slot });
    e.target.value = '';
  };
  const onCropSave = (file) => {
    setFiles((prev) => ({ ...prev, [crop.target]: file }));
    setCrop({ open: false, src: null, target: null });
  };

  const reset = () => {
    setFiles({});
    setValues({});
    setNote('');
    setDate('');
    setCrop({ open: false, src: null, target: null });
  };
  const handleCancel = () => { reset(); onClose(); };

  const anyPhoto = PHOTO_SLOTS.some((s) => files[s.key]);
  const anyParam = PARAM_DEFS.some((p) => values[p.key] !== '' && values[p.key] != null);

  const save = async () => {
    const input = { note: note.trim() || null };
    if (date) input.taken_at = date;
    PARAM_DEFS.forEach((p) => {
      const v = values[p.key];
      if (v !== '' && v != null) input[p.key] = Number(v);
      else if (isEdit) input[p.key] = null; // editar permite limpiar un parámetro
    });
    PHOTO_SLOTS.forEach((s) => { if (files[s.key]) input[s.key] = files[s.key]; });
    try {
      if (isEdit) {
        await update({ variables: { id: entry.id, input } });
      } else {
        await submit({ variables: { input } });
      }
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `phys-${Date.now()}`, variant: 'success', message: isEdit ? 'Progreso actualizado' : 'Progreso guardado' })));
      onSaved();
      onClose();
    } catch (err) { /* el errorHandler muestra el mensaje */ }
  };

  const photoBox = (slot) => (
    <Stack spacing={1}>
      <Box sx={{ position: 'relative', borderRadius: '14px', overflow: 'hidden', border: '1px dashed', borderColor: 'hairline.strong', aspectRatio: '3 / 4', backgroundColor: 'surface.raised' }}>
        {previews[slot.key] ? (
          <Box component="img" src={previews[slot.key]} alt={slot.label} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.disabled' }}>
            <PhotoIcon sx={{ fontSize: 30 }} />
          </Stack>
        )}
        <Box sx={{ position: 'absolute', top: 6, left: 6 }}>
          <Chip size="small" label={slot.label} sx={{ backgroundColor: 'rgba(0,0,0,0.55)', color: '#FFFFFF', fontWeight: 700, letterSpacing: '0.06em', height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.62rem' } }} />
        </Box>
      </Box>
      <Button component="label" size="small" variant="contained" color="primary" fullWidth sx={{ textTransform: 'none', fontWeight: 700 }}>
        {previews[slot.key] ? 'Cambiar' : 'Subir'}
        <input hidden type="file" accept="image/*" onChange={pickFile(slot.key)} />
      </Button>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{isEdit ? 'Editar entrada' : 'Nueva entrada de progreso'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Grid container spacing={1.5}>
            {PHOTO_SLOTS.map((s) => <Grid xs={4} key={s.key}>{photoBox(s)}</Grid>)}
          </Grid>

          <TextField
            size="small" type="date" label="Fecha" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }}
            sx={{ '& input::-webkit-calendar-picker-indicator': { filter: 'invert(80%) sepia(40%) saturate(680%) hue-rotate(350deg) brightness(100%) contrast(95%)', cursor: 'pointer', opacity: 1 } }}
          />

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1 }}>Parámetros</Typography>
            <Grid container spacing={1.5}>
              {PARAM_DEFS.map((p) => (
                <Grid xs={6} sm={4} key={p.key}>
                  <TextField
                    fullWidth size="small" type="number" label={p.label}
                    value={values[p.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [p.key]: e.target.value }))}
                    InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.disabled' }}>{p.unit}</Typography> }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <TextField size="small" label="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} fullWidth multiline minRows={2} inputProps={{ maxLength: 500 }} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={handleCancel} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={save} disabled={loading || (!isEdit && !anyPhoto && !anyParam)} sx={{ textTransform: 'none', fontWeight: 700 }} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}>Guardar</Button>
      </DialogActions>

      <AvatarCropModal open={crop.open} imageSrc={crop.src} aspect={3 / 4} cropShape="rect" title="Ajustar foto" saveLabel="Usar foto" onCancel={() => setCrop({ open: false, src: null, target: null })} onSave={onCropSave} />
    </Dialog>
  );
}
EntryForm.propTypes = { open: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired, entry: PropTypes.object };

/* ----------------------------------- Main ------------------------------------ */
function PhysicalProgress() {
  const { t } = useTranslation(['sessions']);
  const userId = useSelector((s) => s.auth.user.id);
  const { data, loading, refetch } = useQuery(MY_PHYSICAL_PROGRESS, { fetchPolicy: 'cache-and-network' });
  const [del] = useMutation(DELETE_PHYSICAL_PROGRESS);
  const [formOpen, setFormOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const entries = data?.myPhysicalProgress ?? [];
  const canCompare = entries.length >= 2;

  const confirmDelete = async () => {
    if (deleteId == null) return;
    await del({ variables: { id: deleteId } });
    setDeleteId(null);
    refetch();
  };

  let body;
  if (loading && entries.length === 0) {
    body = <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: 'secondary.main' }} /></Box>;
  } else if (entries.length === 0) {
    body = (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <PhotoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1">Aún no tienes registros. Sube tu primera entrada (frente, lateral y espalda).</Typography>
      </Box>
    );
  } else {
    body = (
      <>
        <ProgressCharts entries={entries} />
        <Grid container spacing={2.5}>
          {entries.map((e) => (
            <Grid xs={12} md={6} key={e.id}>
              <ProgressEntryCard entry={e} onEdit={setEditEntry} onDelete={setDeleteId} />
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  return (
    <Box>
      <PhraseBanner />

      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', flexWrap: 'wrap', gap: 0.5 }}>
          <LockIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">Solo tú puedes ver estas fotos.</Typography>
          <Button size="small" startIcon={<ShareIcon fontSize="small" />} onClick={() => setShareOpen(true)} sx={{ color: 'secondary.main', textTransform: 'none', fontWeight: 700 }}>
            Compartir con…
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          {canCompare && (
            <Button variant="outlined" color="inherit" startIcon={<CompareIcon />} onClick={() => setCompareOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, color: 'text.primary', borderColor: 'hairline.strong' }}>
              Comparar
            </Button>
          )}
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setFormOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Nueva entrada
          </Button>
        </Stack>
      </Stack>

      {body}

      {userId && <ProgressObservations clientId={userId} readOnly />}

      {formOpen && <EntryForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={refetch} />}
      {editEntry && <EntryForm open entry={editEntry} onClose={() => setEditEntry(null)} onSaved={refetch} />}
      {compareOpen && <ProgressCompareModal open={compareOpen} onClose={() => setCompareOpen(false)} entries={entries} />}
      <ProgressShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={confirmDelete} count={1} title={t('deletePhysicalTitle', { ns: 'sessions', defaultValue: 'Eliminar registro' })} message={t('deletePhysicalMsg', { ns: 'sessions', defaultValue: '¿Seguro que quieres eliminar este registro de progreso?' })} />
    </Box>
  );
}

export default PhysicalProgress;
